import { useMemo, useState } from 'react';
import { Plus, Pencil, History, UserPlus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { easternDay } from '../../../modules/q2c/eastern-day.mjs';
import Modal from '../ui/Modal';
import { Chip, fmtDate, usd, inputCls, FieldGrid, toPayload } from '../bids/FieldGrid';
import { card, iconBtn, primaryBtn, opts, useList, FormModal, HistoryModal, Empty, EVENT_KIND, EVENT_STATUS, EVENT_TONE, REIMB } from './shared';

const EVENT_FIELDS = [
  { key: 'name', label: 'Event', required: true, span: 2 },
  { key: 'kind', label: 'Type', type: 'select', options: opts(EVENT_KIND), required: true },
  { key: 'status', label: 'Status', type: 'select', options: opts(EVENT_STATUS), required: true },
  { key: 'organizer', label: 'Organizer' },
  { key: 'location', label: 'Location' },
  { key: 'start_date', label: 'Start date', type: 'date' },
  { key: 'end_date', label: 'End date', type: 'date' },
  { key: 'date_note', label: 'Date note', span: 2, hint: 'e.g. tentative, or dates not confirmed' },
  { key: 'cost_total_cents', label: 'Total cost', type: 'money' },
  { key: 'paid_by', label: 'Paid by' },
  { key: 'ccs_share_cents', label: 'CCS share', type: 'money' },
  { key: 'concria_share_cents', label: 'Concria share', type: 'money' },
  { key: 'split_note', label: 'Cost split note', type: 'textarea', rows: 2 },
  { key: 'reimbursement_status', label: 'Concria share status', type: 'select', options: opts(REIMB), empty: 'Not set' },
  { key: 'attendees', label: 'Who went / is going' },
  { key: 'leads_captured', label: 'Leads captured (count)', type: 'number', hint: 'People you add below are counted separately.' },
  { key: 'follow_ups', label: 'Follow-ups', type: 'textarea', rows: 3 },
  { key: 'notes', label: 'Notes', type: 'textarea', rows: 3 },
];

function CaptureModal({ event, options, onDone, onClose }) {
  const [mode, setMode] = useState('new');
  const [v, setV] = useState({ name: '', email: '', company: '', phone: '', note: '', follow_up_due: '' });
  const [link, setLink] = useState({ lead_id: null, account_id: null, contact_id: null, note: '', follow_up_due: null });
  const [err, setErr] = useState('');
  const fields = useMemo(() => [
    { key: 'lead_id', label: 'Existing lead', type: 'picker', options: options.leads.map((l) => ({ value: l.id, label: `${l.name || l.email || 'lead'}${l.company ? ` (${l.company})` : ''}` })), span: 2 },
    { key: 'account_id', label: 'CRM company', type: 'picker', options: options.accounts.map((a) => ({ value: a.id, label: a.name })) },
    { key: 'contact_id', label: 'CRM contact', type: 'picker', options: options.contacts.map((c) => ({ value: c.id, label: `${c.name}${c.email ? ` <${c.email}>` : ''}` })) },
    { key: 'note', label: 'Note', span: 2 },
    { key: 'follow_up_due', label: 'Follow up by', type: 'date' },
  ], [options]);
  async function save() {
    try {
      if (mode === 'new') {
        const body = Object.fromEntries(Object.entries(v).filter(([, x]) => x !== ''));
        await api.post(`/marketing/events/${event.id}/capture`, body);
      } else {
        const { payload } = toPayload(fields, link);
        await api.post(`/marketing/events/${event.id}/leads`, payload);
      }
      onDone();
    } catch (e) { setErr(e.message); }
  }
  const input = (k, label, extra = {}) => (
    <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">{label}</span><input className={inputCls} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} {...extra} /></label>
  );
  return (
    <Modal title={`People met at ${event.name}`} onClose={onClose} maxWidth="max-w-2xl">
      <div className="mb-3 flex gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> New person (creates a CRM lead)</label>
        <label className="flex items-center gap-2"><input type="radio" checked={mode === 'link'} onChange={() => setMode('link')} /> Already in the CRM</label>
      </div>
      {mode === 'new' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {input('name', 'Name *')}{input('company', 'Company')}{input('email', 'Email')}{input('phone', 'Phone')}
          <div className="sm:col-span-2">{input('note', 'Note')}</div>
          {input('follow_up_due', 'Follow up by', { type: 'date' })}
        </div>
      ) : <FieldGrid fields={fields} value={link} onChange={setLink} />}
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button onClick={save} className={primaryBtn}>Add</button>
      </div>
    </Modal>
  );
}

function EventDetail({ ev, options, onClose, onChanged }) {
  const { rows: d, err, load, setErr } = useList(`/marketing/events/${ev.id}`, null);
  const [capture, setCapture] = useState(false);
  const e = d?.event;
  return (
    <Modal title={ev.name} onClose={onClose} maxWidth="max-w-4xl">
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!d && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {e && (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2"><Chip tone={EVENT_TONE[e.status]}>{EVENT_STATUS[e.status]}</Chip><span className="text-[var(--text-secondary)]">{EVENT_KIND[e.kind]}{e.organizer ? ` · ${e.organizer}` : ''}{e.location ? ` · ${e.location}` : ''}</span></div>
          <div>{e.start_date ? `${fmtDate(e.start_date)}${e.end_date && e.end_date !== e.start_date ? ` to ${fmtDate(e.end_date)}` : ''}` : 'Dates not set'}{e.date_note ? ` (${e.date_note})` : ''}</div>
          {(e.cost_total_cents != null || e.split_note) && (
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-800">
              <div>Total {usd(e.cost_total_cents) || 'not recorded'} · CCS {usd(e.ccs_share_cents) || 'not recorded'} · Concria {usd(e.concria_share_cents) || 'not recorded'}{e.paid_by ? ` · paid by ${e.paid_by}` : ''}{e.reimbursement_status ? ` · ${REIMB[e.reimbursement_status]}` : ''}</div>
              {e.split_note && <div className="mt-1 text-xs text-[var(--text-secondary)]">{e.split_note}</div>}
            </div>
          )}
          {e.attendees && <div><span className="text-[var(--text-secondary)]">Attendees:</span> {e.attendees}</div>}
          {e.follow_ups && <div className="whitespace-pre-wrap"><span className="text-[var(--text-secondary)]">Follow-ups:</span> {e.follow_ups}</div>}
          {e.notes && <div className="whitespace-pre-wrap text-[var(--text-secondary)]">{e.notes}</div>}
          {e.sources?.length > 0 && <div className="text-xs text-[var(--text-secondary)]">Sources: {e.sources.join('; ')}</div>}
          <div className="flex items-center justify-between border-t border-[var(--border)] pt-3">
            <h4 className="font-semibold">Leads and follow-ups ({d.leads.length}{e.leads_captured != null ? `, ${e.leads_captured} counted on the day` : ''})</h4>
            <button className={iconBtn} onClick={() => setCapture(true)}><UserPlus size={12} /> Add person</button>
          </div>
          {d.leads.length ? (
            <ul className="space-y-1">
              {d.leads.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border)] p-2">
                  <span>{l.lead?.name || l.contact?.name || l.account_name || l.name}{l.lead?.company ? ` (${l.lead.company})` : ''}{l.note ? <span className="text-xs text-[var(--text-secondary)]"> · {l.note}</span> : null}{l.lead ? <span className="text-xs text-[var(--text-secondary)]"> · lead {l.lead.stage}</span> : null}</span>
                  <span className="flex items-center gap-2 text-xs">
                    {l.follow_up_due && <span className="text-[var(--text-secondary)]">by {fmtDate(l.follow_up_due)}</span>}
                    <label className="flex items-center gap-1"><input type="checkbox" checked={l.follow_up_done} onChange={async (x) => { try { await api.patch(`/marketing/events/leads/${l.id}`, { follow_up_done: x.target.checked }); await load(); onChanged(); } catch (er) { setErr(er.message); } }} /> followed up</label>
                    <button className={iconBtn} title="Remove" onClick={async () => { try { await api.delete(`/marketing/event-leads/${l.id}`); await load(); onChanged(); } catch (er) { setErr(er.message); } }}><Trash2 size={12} /></button>
                  </span>
                </li>
              ))}
            </ul>
          ) : <Empty>No one recorded yet.</Empty>}
        </div>
      )}
      {capture && <CaptureModal event={ev} options={options} onClose={() => setCapture(false)} onDone={async () => { setCapture(false); await load(); onChanged(); }} />}
    </Modal>
  );
}

export default function EventsTab({ options }) {
  const { rows, err, load } = useList('/marketing/events', 'events');
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const [hist, setHist] = useState(null);
  const today = easternDay(new Date());
  const sorted = useMemo(() => {
    const r = rows || [];
    const upcoming = r.filter((e) => (e.start_date && (e.end_date || e.start_date) >= today) || (!e.start_date && ['considering', 'planned', 'registered'].includes(e.status)))
      .sort((a, b) => String(a.start_date || '9999').localeCompare(String(b.start_date || '9999')));
    const past = r.filter((e) => !upcoming.includes(e));
    return { upcoming, past };
  }, [rows, today]);
  const save = async (payload) => {
    if (edit.id) await api.patch(`/marketing/events/${edit.id}`, payload); else await api.post('/marketing/events', payload);
    setEdit(null); await load();
  };
  const Row = ({ e }) => (
    <tr className="border-t border-[var(--border)] align-top">
      <td className="py-2 pr-2"><button className="text-left font-medium hover:underline" onClick={() => setOpen(e)}>{e.name}</button><div className="text-xs text-[var(--text-secondary)]">{EVENT_KIND[e.kind]}{e.location ? ` · ${e.location}` : ''}</div></td>
      <td className="pr-2 text-xs">{e.start_date ? fmtDate(e.start_date) : <span className="text-[var(--text-secondary)]">{e.date_note || 'Not set'}</span>}</td>
      <td className="pr-2"><Chip tone={EVENT_TONE[e.status]}>{EVENT_STATUS[e.status]}</Chip></td>
      <td className="pr-2 text-xs">{usd(e.cost_total_cents)}{e.ccs_share_cents != null && <div className="text-[var(--text-secondary)]">CCS {usd(e.ccs_share_cents)} / Concria {usd(e.concria_share_cents)}</div>}</td>
      <td className="pr-2 text-xs">{e.linked_leads}{e.leads_captured != null ? ` (+${e.leads_captured} counted)` : ''}{e.open_follow_ups ? <div className="text-amber-700 dark:text-amber-300">{e.open_follow_ups} to follow up</div> : null}</td>
      <td className="whitespace-nowrap"><button className={iconBtn} onClick={() => setEdit(e)}><Pencil size={12} /></button> <button className={iconBtn} onClick={() => setHist(e)}><History size={12} /></button></td>
    </tr>
  );
  const Table = ({ list }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1">Event</th><th>Dates</th><th>Status</th><th>Cost</th><th>Leads</th><th /></tr></thead>
        <tbody>{list.map((e) => <Row key={e.id} e={e} />)}</tbody>
      </table>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-[var(--text-secondary)]">Trade shows, conferences, trainings and sponsorships, with the CCS / Concria cost split. Blank means not in the evidence yet.</p>
        <button className={primaryBtn} onClick={() => setEdit({ status: 'considering', kind: 'trade_show' })}><Plus size={14} /> New event</button>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && (
        <>
          <div className={card}><h3 className="mb-2 text-sm font-semibold">Coming up</h3>{sorted.upcoming.length ? <Table list={sorted.upcoming} /> : <Empty>Nothing planned.</Empty>}</div>
          <div className={card}><h3 className="mb-2 text-sm font-semibold">Past and undecided</h3>{sorted.past.length ? <Table list={sorted.past} /> : <Empty>None.</Empty>}</div>
        </>
      )}
      {edit && <FormModal title={edit.id ? 'Edit event' : 'New event'} fields={EVENT_FIELDS} record={edit} onSave={save} onClose={() => setEdit(null)} />}
      {open && <EventDetail ev={open} options={options} onClose={() => setOpen(null)} onChanged={load} />}
      {hist && <HistoryModal table="mkt_events" id={hist.id} title={hist.name} onClose={() => setHist(null)} />}
    </div>
  );
}
